import React, { useState } from "react";
import { Button, Form } from "react-bootstrap";
import { toast } from "react-toastify";
import { apiSetRestaurantDailyAvailability } from "../api";
import { useGate, locationRefusal } from "./team/Gated";
import { useVendorAccess } from "../hooks/useVendorAccess";
import { useBranchScope } from "../context/BranchScopeContext";
import { branchAwareMessage } from "../utilities/vendorScope";

const DEFAULT_TITLE = "Sold out for the day: counts plates ordered on every channel and resets each business day";
// Head office with branches and no branch picked: the change reaches every
// branch, so the control says so (4 Oct 2026).
export const EVERYWHERE_TITLE =
  "This marks the dish sold out today at every branch. To mark it at one branch only, pick that branch at the top of the page.";
// A member who runs several branches and is looking at all of them at once.
export const PICK_BRANCH_TITLE = "Pick one of your branches at the top of the page to mark a dish sold out there.";

/**
 * Sold out for the day, per dish, on the vendor menu.
 *
 * Reads the fields GET /restaurants/menu-sections now returns on every product
 * (dailyLimit, todaysLimit, soldOutToday, soldOutReason, platesLeftToday) and
 * offers the two things a kitchen actually does mid-service: mark a dish sold
 * out now, and put it back (raising today's number when the count is what
 * sold it out). The standing daily limit lives on the edit form.
 *
 * Every change is broadcast by the server to the storefronts, the terminal
 * and the WhatsApp menu, so the vendor sees the same state their customers do.
 *
 * With a branch picked in the navbar (4 Oct 2026) the control acts on that
 * branch only: `location` is sent with the change and `branchState` (the
 * branch's StateDTO, or null for none) is what it shows. A branch can add a
 * sold out but cannot lift the restaurant-wide one, and plate limits stay
 * restaurant-wide, so neither is offered at a branch. With "All branches"
 * it works on the dish everywhere, exactly as before, and for a restaurant
 * with branches it says "at every branch" so nobody mistakes it for one.
 * A restaurant without branches sees exactly what it always has.
 */
export default function DailyPlateControl({ product, onChanged, location = null, locationName = null, branchState = null }) {
  const [saving, setSaving] = useState(false);
  // Daily availability is an activation change (vendor team catalogue:activate),
  // at the branch when one is picked.
  const gate = useGate("catalogue:activate");
  const access = useVendorAccess();
  const { isBranchScoped } = access;
  const { hasBranches } = useBranchScope();
  const atBranch = Boolean(location);
  // Why the control is locked, in the words for the real cause.
  const refusal = atBranch
    ? locationRefusal(access, "catalogue:activate", location)
    : isBranchScoped
      ? PICK_BRANCH_TITLE
      : gate.allowed
        ? undefined
        : gate.title;
  const allowed = !refusal;
  const busy = saving || !allowed;
  // Head office acting on every branch at once.
  const everywhere = !atBranch && hasBranches;
  const [raising, setRaising] = useState(false);
  const [newLimit, setNewLimit] = useState("");

  const hasLimit = Number.isFinite(Number(product?.todaysLimit)) && product?.todaysLimit !== null;
  const soldOutEverywhere = product?.soldOutToday === true;
  const soldOutHere = atBranch && branchState?.soldOut?.live === true;
  // A branch sold out set "Until I switch it back" on the Branches tab does
  // not clear itself tomorrow, so it is not called "today".
  const untilBack = soldOutHere && branchState?.soldOut?.until === "UNTIL_BACK";
  const soldOut = soldOutEverywhere || soldOutHere;
  const byCount = product?.soldOutReason === "DAILY_LIMIT";
  const left = product?.platesLeftToday;
  const where = locationName || "this branch";

  const submit = async (body, successText) => {
    setSaving(true);
    try {
      const { data } = await apiSetRestaurantDailyAvailability(product._id, atBranch ? { ...body, location } : body);
      if (data?.stillSoldOutByCount) {
        setRaising(true);
        setNewLimit(String((data?.availability?.todaysLimit ?? 0) + 5));
        toast.info(`"${product.name}" is still at today's limit. Raise today's number to put it back on sale.`);
      } else {
        setRaising(false);
        if (successText) toast.success(successText);
      }
      if (typeof onChanged === "function") onChanged(data);
    } catch (err) {
      toast.error(branchAwareMessage(err, "Could not update today's availability"));
    } finally {
      setSaving(false);
    }
  };

  const stop = (e) => e.stopPropagation();

  return (
    <div
      onClick={stop}
      className="d-flex align-items-center flex-wrap"
      style={{ gap: 6, marginRight: 8 }}
      title={
        refusal ||
        (atBranch && soldOutEverywhere
          ? isBranchScoped
            ? "Head office marked this sold out at every branch today."
            : "Sold out at every branch today. Pick All branches to put it back on sale."
          : everywhere
            ? EVERYWHERE_TITLE
            : DEFAULT_TITLE)
      }
    >
      {soldOut ? (
        <span style={{ fontSize: 12, color: "#c9402f", fontWeight: 600, whiteSpace: "nowrap" }}>
          {soldOutHere && !soldOutEverywhere
            ? untilBack ? `Sold out at ${where}` : `Sold out today at ${where}`
            : `Sold out today${everywhere ? " at every branch" : ""}${byCount ? " (limit reached)" : ""}`}
        </span>
      ) : hasLimit ? (
        <span style={{ fontSize: 12, color: left <= 3 ? "#b8860b" : "#6c757d", whiteSpace: "nowrap" }}>
          {left} of {product.todaysLimit} plates left
        </span>
      ) : null}

      {soldOut ? (
        <Button
          size="sm"
          variant="outline-success"
          disabled={busy || (atBranch && soldOutEverywhere)}
          onClick={() =>
            submit(
              { soldOutToday: false },
              atBranch
                ? `"${product.name}" is back on sale at ${where}`
                : `"${product.name}" is back on sale${everywhere ? " at every branch" : ""}`
            )
          }
        >
          Back on sale
        </Button>
      ) : (
        <Button
          size="sm"
          variant="outline-danger"
          disabled={busy}
          onClick={() =>
            submit(
              { soldOutToday: true },
              atBranch
                ? `"${product.name}" sold out today at ${where}`
                : `"${product.name}" marked sold out for today${everywhere ? " at every branch" : ""}`
            )
          }
        >
          Sold out today
        </Button>
      )}

      {!atBranch && (raising || (soldOut && byCount)) && (
        <Form
          className="d-flex align-items-center"
          style={{ gap: 4 }}
          onSubmit={(e) => {
            e.preventDefault();
            const n = Math.round(Number(newLimit));
            if (!Number.isFinite(n) || n < 1) {
              toast.error("Today's limit must be a whole number of at least 1");
              return;
            }
            submit({ todaysLimit: n, soldOutToday: false }, `Today's limit for "${product.name}" is now ${n}`);
          }}
        >
          <Form.Control
            size="sm"
            type="number"
            min="1"
            step="1"
            style={{ width: 84 }}
            placeholder="Today"
            value={newLimit}
            onChange={(e) => setNewLimit(e.target.value)}
            aria-label="Today's plate limit"
          />
          <Button size="sm" variant="outline-primary" type="submit" disabled={busy}>
            Raise
          </Button>
        </Form>
      )}
    </div>
  );
}
